'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { getCategoryUrl } from '@/lib/articles';

interface SubcategoryTabsProps {
  categoryId: string;
  subcategories: { name: string; count: number }[];
}

export function SubcategoryTabs({ categoryId, subcategories }: SubcategoryTabsProps) {
  const pathname = usePathname();
  
  // URL: /sports/football -> parts: ['sports', 'football']
  const pathParts = pathname.split('/').filter(Boolean);
  const currentSubSlug = pathParts.length > 1 ? pathParts[1] : null;

  if (subcategories.length === 0) return null;

  return (
    <div className="relative mb-8 w-full">
      <ScrollArea className="w-full whitespace-nowrap pb-4">
        <div className="flex w-max space-x-2 p-1">
          <Link href={getCategoryUrl(categoryId)}>
            <Badge 
              variant="outline" 
              className={cn(
                "cursor-pointer px-4 py-2 text-sm font-medium transition-all text-foreground hover:bg-foreground/5",
                !currentSubSlug 
                  ? "border-primary border-2 shadow-md bg-primary/5 text-primary" 
                  : "border-foreground"
              )}
            >
              All {categoryId}
            </Badge>
          </Link>
          
          {subcategories.map((sub) => {
            const subSlug = sub.name.toLowerCase().trim().replace(/\s+/g, '-');
            const isActive = currentSubSlug === subSlug;
            
            return (
              <Link 
                key={sub.name} 
                href={getCategoryUrl(categoryId, sub.name)}
              >
                <Badge 
                  variant="outline" 
                  className={cn(
                    "cursor-pointer px-4 py-2 text-sm font-medium transition-all text-foreground hover:bg-foreground/5",
                    isActive 
                      ? "border-primary border-2 shadow-md bg-primary/5 text-primary" 
                      : "border-foreground"
                  )}
                >
                  {sub.name}
                </Badge>
              </Link>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" className="h-2 mt-2 bg-muted/20" />
      </ScrollArea>
    </div>
  );
}